/**
 * crypto.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

export const HASH_ALG = "SHA-512";
export const CIPHER_ALG = "AES-GCM";
export const KDF_ALG = "HKDF";
export const EXTRACT_ALG = "HMAC";
export const EC_CURVE = "P-384";

import * as pqe from "@secured/mercury-crypto";
import {
  initCrypto, VirgilCrypto, KeyPairType,
  VirgilKeyPair, VirgilPublicKey, VirgilPrivateKey
} from "virgil-crypto";

import { bufferToHex } from "../services/encoding.service";

export interface CryptoService {
  init(): Promise<any>,

  generateNewKeyPair(): VirgilKeyPair,
  deriveKeyPair(secret: ArrayBufferLike): VirgilKeyPair,
  encapsulate(secret: ArrayBufferLike, publicKey: VirgilPublicKey): Uint8Array,
  decapsulate(secret: Uint8Array | string, privateKey: VirgilPrivateKey): Uint8Array,
  sign(data: Uint8Array, privateKey: VirgilPrivateKey): Uint8Array,
  verifySignature(data: Uint8Array, signature: Uint8Array, publicKey: VirgilPublicKey): boolean,
  importPublicKey(publicKey: ArrayBufferLike | string): VirgilPublicKey,
  importPrivateKey(privateKey: ArrayBufferLike | string): VirgilPrivateKey,
  publicKeyToBuffer(publicKey: VirgilPublicKey): Uint8Array,
  privateKeyToBuffer(privateKey: VirgilPrivateKey): Uint8Array,

  generateKyberKeyPair(): pqe.KyberKeyPair,
  deriveKyberKeyPair(secret: Uint8Array): pqe.KyberKeyPair,
  importKyberPubKey(data: string | Uint8Array): pqe.KyberPublicKey,
  importKyberPriKey(data: string | Uint8Array): pqe.KyberPrivateKey,
  kyberEncrypt(secret: Uint8Array, publicKey: pqe.KyberPublicKey): Uint8Array,
  kyberDecrypt(data: Uint8Array, privateKey: pqe.KyberPrivateKey): Uint8Array,

  generateSignKeyPair(): pqe.DilithiumKeyPair,
  deriveDilithiumKeyPair(secret: Uint8Array): pqe.DilithiumKeyPair,
  importDilithiumPubKey(data: string | Uint8Array): pqe.DilithiumPublicKey,
  importDilithiumPriKey(data: string | Uint8Array): pqe.DilithiumPrivateKey,
  dilithiumSign(data: Uint8Array, privateKey: pqe.DilithiumPrivateKey): Uint8Array,
  dilithiumVerify(signature: Uint8Array, data: Uint8Array, publicKey: pqe.DilithiumPublicKey): boolean,

  generateRandSecret(): Promise<Uint8Array>,
  deriveSecret(secret: ArrayBufferLike, info?: string): Promise<Uint8Array>,
  deriveKDFSecret(secret: ArrayBufferLike, salt: ArrayBufferLike, info?: string): Promise<Uint8Array>,
  deriveTreeSecret(secret: ArrayBuffer, label: string, index: number, generation: number): Promise<Uint8Array>,
  seal(message: ArrayBuffer, encryptKey: ArrayBuffer | CryptoKey, nonce: ArrayBuffer, additionalData?: ArrayBufferLike): Promise<Uint8Array>,
  open(encrypted: ArrayBuffer, encryptKey: ArrayBuffer | CryptoKey, nonce: ArrayBuffer, additionalData?: ArrayBufferLike): Promise<Uint8Array>,
  importCryptoKey(key: ArrayBuffer): Promise<CryptoKey>,
  //hash(data: ArrayBufferLike): Promise<string>
  hash256(data: ArrayBufferLike): Promise<ArrayBuffer>
}

class CryptoServiceImpl implements CryptoService {

  private virgil: VirgilCrypto;

  constructor() {
    this.virgil = new VirgilCrypto();
  }

  init(): Promise<any> {
    return Promise.all([initCrypto(), pqe.initCrystals()]);
  }

  generateNewKeyPair(): VirgilKeyPair {
    return this.virgil.generateKeys(KeyPairType.SECP256R1);
  }

  deriveKeyPair(secret: ArrayBuffer): VirgilKeyPair {
    //const buf = await this.deriveKDFSecret(secret, salt, info);
    return this.virgil.generateKeysFromKeyMaterial(
      new Uint8Array(secret), KeyPairType.SECP256R1);
  }

  encapsulate(secret: ArrayBufferLike, publicKey: VirgilPublicKey, signKey?: VirgilPrivateKey): Uint8Array {
    if (signKey) {
      return this.virgil.signThenEncrypt(new Uint8Array(secret), signKey, publicKey);
    }

    return this.virgil.encrypt(new Uint8Array(secret), publicKey);
  }

  decapsulate(secret: Uint8Array | string, privateKey: VirgilPrivateKey, publicKey?: VirgilPublicKey): Uint8Array {
    if (publicKey) {
      return this.virgil.decryptThenVerify(secret, privateKey, publicKey);
    }
    return this.virgil.decrypt(secret, privateKey);
  }

  sign(data: Uint8Array, privateKey: VirgilPrivateKey): Uint8Array {
    return this.virgil.calculateSignature(data, privateKey);
  }

  verifySignature(data: Uint8Array, signature: Uint8Array, publicKey: VirgilPublicKey): boolean {
    return this.virgil.verifySignature(data, signature, publicKey);
  }

  generateKyberKeyPair(): pqe.KyberKeyPair { return pqe.generateKeyPair(); }
  deriveKyberKeyPair(secret: Uint8Array): pqe.KyberKeyPair {
    return pqe.generateKeyPairFrom(secret);
  }
  importKyberPubKey(data: string | Uint8Array): pqe.KyberPublicKey {
    return pqe.publicKeyFrom(data);
  }
  importKyberPriKey(data: string | Uint8Array): pqe.KyberPrivateKey {
    return pqe.privateKeyFrom(data);
  }
  kyberEncrypt(secret: Uint8Array, publicKey: pqe.KyberPublicKey): Uint8Array {
    return pqe.encrypt(secret, publicKey);
  }
  kyberDecrypt(data: Uint8Array, privateKey: pqe.KyberPrivateKey): Uint8Array {
    return pqe.decrypt(data, privateKey);
  }

  generateSignKeyPair(): pqe.DilithiumKeyPair { return pqe.generateSigningKeyPair(); }
  deriveDilithiumKeyPair(secret: Uint8Array): pqe.DilithiumKeyPair {
    return pqe.generateSigningKeyPairFrom(secret);
  }
  dilithiumSign(data: Uint8Array, privateKey: pqe.DilithiumPrivateKey): Uint8Array {
    return pqe.sign(data, privateKey);
  }
  dilithiumVerify(signature: Uint8Array, data: Uint8Array, publicKey: pqe.DilithiumPublicKey): boolean {
    return pqe.verify(signature, data, publicKey);
  }
  importDilithiumPubKey(data: string | Uint8Array): pqe.DilithiumPublicKey {
    return pqe.publicSigningKeyFrom(data);
  }
  importDilithiumPriKey(data: string | Uint8Array): pqe.DilithiumPrivateKey {
    return pqe.privateSigningKeyFrom(data);
  }

  async generateRandSecret(): Promise<Uint8Array> {
    // let key = await window.crypto.subtle.importKey(
    //   "raw", new TextEncoder().encode(from), {name: CIPHER_ALG}, true,
    //   ["encrypt", "decrypt", "wrapKey", "unwrapKey"])
    // return window.crypto.subtle.exportKey("raw", key);
    return new Uint8Array(await crypto.subtle.exportKey("raw",
      await crypto.subtle.generateKey(
        { name: CIPHER_ALG, length: 256 },
        true,
        ["encrypt", "decrypt", "wrapKey", "unwrapKey"])));
  }

  async deriveKDFSecret(secret: ArrayBuffer, salt: ArrayBufferLike, info = ""): Promise<Uint8Array> {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      "raw", secret, { name: KDF_ALG }, false,
      ["deriveKey", "deriveBits"])
    return new Uint8Array(await crypto.subtle.deriveBits(
      {
        name: KDF_ALG,
        hash: { name: HASH_ALG },
        salt: salt,
        info: encoder.encode(info)
      },
      key,
      256));
  }

  deriveSecret(secret: ArrayBufferLike, info: string): Promise<Uint8Array> {
    //return this.expand(secret, `mls10 ${info}`, 32);
    return this.deriveKDFSecret(secret, new ArrayBuffer(0), `mls10 ${info}`);
  }

  async deriveTreeSecret(secret: ArrayBuffer, label: string, index: number, generation: number): Promise<Uint8Array> {
    const encoder = new TextEncoder();
    const salt = new ArrayBuffer(8);
    const view = new DataView(salt);
    view.setUint32(0, index, true);
    view.setUint32(4, generation, true);

    const key = await crypto.subtle.importKey(
      "raw", secret, { name: KDF_ALG }, false,
      ["deriveKey", "deriveBits"])
    return new Uint8Array(await crypto.subtle.deriveBits(
      {
        name: KDF_ALG,
        hash: { name: HASH_ALG },
        salt: salt,
        info: encoder.encode(label)
      },
      key,
      256));
  }

  async seal(message: ArrayBuffer, encryptKey: ArrayBuffer | CryptoKey,
    nonce: ArrayBuffer, additionalData?: ArrayBufferLike): Promise<Uint8Array> {
    if (!(encryptKey instanceof CryptoKey)) {
      encryptKey = await crypto.subtle.importKey(
        "raw", encryptKey,
        { name: CIPHER_ALG, length: 256 },
        false,
        ["encrypt", "decrypt"]);
    }
    const options: AesGcmParams = {
      name: CIPHER_ALG,
      iv: nonce
    };
    if (additionalData) {
      options.additionalData = additionalData;
      options.tagLength = 128;
    }
    return new Uint8Array(await crypto.subtle.encrypt(
      options, encryptKey, message));
  }

  async open(encrypted: ArrayBuffer, decryptKey: ArrayBuffer | CryptoKey,
    nonce: ArrayBuffer, additionalData?: ArrayBufferLike): Promise<Uint8Array> {
    if (!(decryptKey instanceof CryptoKey)) {
      decryptKey = await crypto.subtle.importKey(
        "raw", decryptKey,
        { name: CIPHER_ALG, length: 256 },
        false,
        ["encrypt", "decrypt"]);
    }
    const options: AesGcmParams = {
      name: CIPHER_ALG,
      iv: nonce
    };
    if (additionalData) {
      options.additionalData = additionalData;
      options.tagLength = 128;
    }
    return new Uint8Array(await crypto.subtle.decrypt(
      options, decryptKey, encrypted));
  }

  async extract(salt: string | ArrayBuffer, ikm: string | ArrayBuffer): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    if (typeof salt === "string") {
      salt = encoder.encode(salt);
    }
    if (typeof ikm === "string") {
      ikm = encoder.encode(ikm);
    }
    const key = await crypto.subtle.importKey(
      "raw",
      salt,
      { name: EXTRACT_ALG, hash: { name: HASH_ALG } },
      false,
      ["sign", "verify"]);

    const sig = await crypto.subtle.sign(
      EXTRACT_ALG, key, ikm);

    return sig;
  }

  async expand(secret: ArrayBuffer | CryptoKey, info: string, length: number): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();

    const hashSize = 512 / 8;
    const infoBuf = encoder.encode(info || "");
    const infoLen = infoBuf.length;

    const steps = Math.ceil(length / hashSize);

    if (steps > 0xFF) {
      throw new Error(`OKM length ${length} is too long for ${HASH_ALG} hash`);
    }

    if (secret instanceof ArrayBuffer) {
      secret = await crypto.subtle.importKey("raw", secret,
        { name: EXTRACT_ALG, hash: { name: HASH_ALG } }, false, ["sign", "verify"]);
    }

    const buf = new ArrayBuffer(hashSize * steps + infoLen + 1);
    let dataView: Uint8Array;
    for (let c = 1, start = 0, end = 0; c <= steps; ++c) {

      // use empty space in buf as a temporary holding buffer
      dataView = new Uint8Array(buf, start);
      dataView.set(infoBuf, end);
      dataView[end + infoLen] = c;

      const arr = new Uint8Array(await crypto.subtle.sign(
        { name: EXTRACT_ALG }, secret, dataView));
      dataView.set(arr);

      start = end;
      end += hashSize;
    }

    return buf.slice(0, length);
  }

  async deriveKeyFromText(secret: string, salt: string, info = ""): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const leafkey = await crypto.subtle.importKey(
      "raw", encoder.encode(secret), { name: KDF_ALG }, false,
      ["deriveKey", "deriveBits"]);

    return await crypto.subtle.deriveKey(
      {
        name: KDF_ALG,
        hash: { name: HASH_ALG },
        salt: encoder.encode(salt),
        info: encoder.encode(info)
      },
      leafkey,
      { name: CIPHER_ALG, length: 256 },
      true,
      ["encrypt", "decrypt"]);
  }

  importPublicKey(publicKey: Uint8Array | string): VirgilPublicKey {
    if (publicKey instanceof ArrayBuffer) {
      publicKey = new Uint8Array(publicKey);
    }
    return this.virgil.importPublicKey(publicKey);
  }
  importPrivateKey(privateKey: Uint8Array | string): VirgilPrivateKey {
    if (privateKey instanceof ArrayBuffer) {
      privateKey = new Uint8Array(privateKey);
    }
    return this.virgil.importPrivateKey(privateKey);
  }

  publicKeyToBuffer(publicKey: VirgilPublicKey): Uint8Array {
    return this.virgil.exportPublicKey(publicKey);
  }
  privateKeyToBuffer(privateKey: VirgilPrivateKey): Uint8Array {
    return this.virgil.exportPrivateKey(privateKey);
  }

  hash256(data: ArrayBufferLike): Promise<ArrayBuffer> {
    return crypto.subtle.digest({ name: "SHA-256" }, data);
  }

  async hash(data: ArrayBufferLike): Promise<string> {
    const buf = await crypto.subtle.digest({ name: HASH_ALG }, data);
    return bufferToHex(buf);
  }

  importCryptoKey(key: ArrayBuffer): Promise<CryptoKey> {
    return crypto.subtle.importKey(
      "raw", key,
      { name: CIPHER_ALG, length: 256 },
      false,
      ["encrypt", "decrypt"]);
  }
}

let cryptoService: CryptoService;

export function cryptoServiceFactory(): CryptoService {
  if (!cryptoService) {
    cryptoService = new CryptoServiceImpl();
  }
  return cryptoService;
}
